import { useCallback, useRef, useState } from 'react';
import { css } from 'styled-components';

const BOTTOM_TOLERANCE = 4;
const STICKY_FOOTER_HEIGHT = '--sticky-footer-height';

export const focusClearsStickyFooter = css`
  & :focus {
    scroll-margin-bottom: var(${STICKY_FOOTER_HEIGHT}, 0px);
  }
`;

const getScrollParent = (el: HTMLElement | null): HTMLElement | null => {
  let node = el?.parentElement ?? null;
  while (node) {
    if (/auto|scroll/.test(getComputedStyle(node).overflowY)) return node;
    node = node.parentElement;
  }
  return null;
};

const isStickyFooter = (element: Element): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false;
  const { position, bottom } = getComputedStyle(element);
  return position === 'sticky' && !Number.isNaN(parseFloat(bottom));
};

const stickyFooters = (root: HTMLElement, probe: HTMLElement) =>
  Array.from(new Set([...Array.from(root.children), ...Array.from(probe.parentElement?.children ?? [])])).filter(
    isStickyFooter
  );

const useScrollShadows = () => {
  const [headerShadow, setHeaderShadow] = useState(false);
  const [footerShadow, setFooterShadow] = useState(false);
  const cleanupRef = useRef<(() => void) | undefined>(undefined);

  const probeRef = useCallback((probe: HTMLDivElement | null) => {
    cleanupRef.current?.();
    cleanupRef.current = undefined;
    if (!probe) {
      setHeaderShadow(false);
      setFooterShadow(false);
      return;
    }
    const root = getScrollParent(probe);
    if (!root) return;

    const update = () => {
      setHeaderShadow(root.scrollTop > 0);
      setFooterShadow(root.scrollTop + root.clientHeight < root.scrollHeight - BOTTOM_TOLERANCE);
    };

    const resize = () => {
      update();
      const footers = stickyFooters(root, probe);
      const footerHeight = footers.reduce((height, footer) => height + footer.offsetHeight, 0);
      root.style.setProperty(STICKY_FOOTER_HEIGHT, footerHeight ? `${footerHeight}px` : '');
      footers.forEach((footer) => footer.style.setProperty(STICKY_FOOTER_HEIGHT, '0px'));
    };

    resize();
    root.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(resize);
    observer.observe(root);
    Array.from(root.children).forEach((child) => observer.observe(child));
    if (probe.parentElement && probe.parentElement !== root) observer.observe(probe.parentElement);

    cleanupRef.current = () => {
      root.removeEventListener('scroll', update);
      observer.disconnect();
    };
  }, []);

  return { probeRef, headerShadow, footerShadow };
};

export default useScrollShadows;
